import { Injectable } from '@angular/core';

/** What this device remembers about one account's passkey. */
export interface PasskeyDeviceHint {
  /** Lower-cased account email, used to prefill the email field. */
  email: string;
  /** Set once a passkey was enrolled on this device and verified by the server. */
  enrolledAt?: string;
  /** Last successful passkey login on this device; drives the auto-login order. */
  lastUsedAt?: string;
  /**
   * Consecutive automatic logins that ended without a token. WebAuthn cannot
   * tell "the passkey is gone from this device" from "the user dismissed the
   * prompt", so the counter is what stops a hint that can no longer be
   * satisfied - a passkey deleted in the OS keychain, say - from opening a
   * prompt on every single visit.
   */
  autoLoginFailures?: number;
  /** When the last automatic attempt started, for the cooldown below. */
  autoLoginAttemptedAt?: string;
}

/**
 * Remembers, per browser, which accounts have a passkey on *this* device.
 *
 * The platform gives us no way to ask that question: `passkeys/login/begin` is
 * public and deliberately returns an empty allow-list to avoid user
 * enumeration, and `GET /passkeys` needs a bearer token the login page does not
 * have yet. Without a local hint the login page would have to fire a WebAuthn
 * prompt at every visitor - including the ones who have no passkey at all - so
 * the hint is what makes the automatic prompt on landing safe to do.
 *
 * Only a login hint is stored, nothing secret: the email, and when the passkey
 * was enrolled / last used. It never leaves the device, and it is not proof of
 * anything - every ceremony is still verified server-side.
 */
@Injectable({ providedIn: 'root' })
export class PasskeyDeviceHintsService {
  private static readonly storageKey = 'smzh.passkeyDeviceHints';
  /** Session-scoped, so it lasts exactly until the tab is closed or reused. */
  private static readonly suppressionKey = 'smzh.passkeyAutoLoginSuppressed';
  /**
   * Also session-scoped: a declined offer is a "not now", not a "never". The
   * mobile client keeps the same answer in memory for one app run and asks
   * again on the next, and a browsing session is the web's equivalent.
   */
  private static readonly declinedKey = 'smzh.passkeyEnrolmentDeclined';
  /**
   * The address of the signed-in account, kept so a passkey enrolled later -
   * from the settings card, say - can still be labelled with it. Written from
   * the address the user typed at login, which is known even when the
   * authenticated-user record carries none, and cleared on sign-out.
   */
  private static readonly accountEmailKey = 'smzh.passkeyAccountEmail';
  /** Keeps a shared browser from growing an unbounded list of past users. */
  private static readonly maxEntries = 5;
  /** Automatic attempts a hint gets before it stops opening the prompt. */
  private static readonly maxAutoLoginFailures = 3;
  /**
   * Quiet period between automatic attempts, matching the mobile app's
   * biometric gate: a quick reload should not open a second prompt.
   */
  private static readonly autoLoginCooldownMs = 3000;

  /** Hints for accounts with a passkey on this device, most recently used first. */
  enrolledHints(): PasskeyDeviceHint[] {
    return this.read()
      .filter(
        (hint) =>
          Boolean(hint.enrolledAt) &&
          (hint.autoLoginFailures ?? 0) <
            PasskeyDeviceHintsService.maxAutoLoginFailures,
      )
      .sort((a, b) => this.timestamp(b) - this.timestamp(a));
  }

  /** The account to offer an automatic passkey login to, if any. */
  preferredEmail(): string | null {
    return this.enrolledHints()[0]?.email ?? null;
  }

  /** Whether an automatic attempt was made too recently to make another. */
  isAutoLoginOnCooldown(email: string): boolean {
    const attempted = this.hintFor(email)?.autoLoginAttemptedAt;
    const parsed = attempted ? Date.parse(attempted) : NaN;

    if (Number.isNaN(parsed)) {
      return false;
    }

    return Date.now() - parsed < PasskeyDeviceHintsService.autoLoginCooldownMs;
  }

  /** Starts the cooldown for the next automatic attempt. */
  rememberAutoLoginAttempt(email: string): void {
    this.update(email, (hint) => ({
      ...hint,
      autoLoginAttemptedAt: new Date().toISOString(),
    }));
  }

  hintFor(email: string): PasskeyDeviceHint | null {
    const normalized = this.normalize(email);
    if (!normalized) {
      return null;
    }

    return this.read().find((hint) => hint.email === normalized) ?? null;
  }

  /** Records a passkey enrolled on this device and verified by the server. */
  rememberEnrolment(email: string): void {
    const now = new Date().toISOString();
    this.update(email, (hint) => ({
      ...hint,
      enrolledAt: hint.enrolledAt ?? now,
      lastUsedAt: now,
      autoLoginFailures: 0,
    }));
  }

  /** Records a successful passkey login, so this account is offered first. */
  rememberLogin(email: string): void {
    const now = new Date().toISOString();
    this.update(email, (hint) => ({
      ...hint,
      // A successful assertion proves the passkey is on this device, even when
      // it was enrolled elsewhere and synced in by the password manager.
      enrolledAt: hint.enrolledAt ?? now,
      lastUsedAt: now,
      autoLoginFailures: 0,
    }));
  }

  /**
   * Records an automatic login that produced no token. After
   * {@link maxAutoLoginFailures} of them the hint stops opening the prompt on
   * landing; the passkey button and the autofill offer still work.
   */
  rememberAutoLoginFailure(email: string): void {
    this.update(email, (hint) => ({
      ...hint,
      autoLoginFailures: (hint.autoLoginFailures ?? 0) + 1,
    }));
  }

  /**
   * Suppresses the next automatic passkey login on the login page.
   *
   * Set when the user is signed out: landing back on the login page would
   * otherwise open the prompt at once - and with a working passkey it would
   * sign them straight back in, so logging out would look broken. The passkey
   * button and the autofill offer stay available.
   */
  suppressNextAutoLogin(): void {
    try {
      sessionStorage.setItem(PasskeyDeviceHintsService.suppressionKey, '1');
    } catch {
      // Without session storage the prompt simply appears; the user can dismiss it.
    }
  }

  /** Reads and clears the suppression flag. */
  consumeAutoLoginSuppression(): boolean {
    try {
      const suppressed =
        sessionStorage.getItem(PasskeyDeviceHintsService.suppressionKey) === '1';
      sessionStorage.removeItem(PasskeyDeviceHintsService.suppressionKey);

      return suppressed;
    } catch {
      return false;
    }
  }

  /**
   * Records a declined enrolment offer for this browsing session, so the user
   * is not asked twice on the way to the dashboard. A new session asks again.
   */
  rememberDecline(email: string): void {
    const normalized = this.normalize(email);
    if (!normalized) {
      return;
    }

    try {
      sessionStorage.setItem(
        PasskeyDeviceHintsService.declinedKey,
        normalized,
      );
    } catch {
      // Without session storage the offer may appear twice in one session.
    }
  }

  /** Whether this account already turned the offer down in this session. */
  hasDeclinedThisSession(email: string): boolean {
    const normalized = this.normalize(email);
    if (!normalized) {
      return false;
    }

    try {
      return (
        sessionStorage.getItem(PasskeyDeviceHintsService.declinedKey) ===
        normalized
      );
    } catch {
      return false;
    }
  }

  /** Records the signed-in address, for labelling a passkey enrolled later. */
  rememberAccountEmail(email: string): void {
    const normalized = this.normalize(email);
    if (!normalized) {
      return;
    }

    try {
      localStorage.setItem(
        PasskeyDeviceHintsService.accountEmailKey,
        normalized,
      );
    } catch {
      // The label then falls back to whatever the server sends.
    }
  }

  /** The signed-in address, or null when none was recorded. */
  accountEmail(): string | null {
    try {
      return localStorage.getItem(PasskeyDeviceHintsService.accountEmailKey);
    } catch {
      return null;
    }
  }

  /** Drops the signed-in address; called on sign-out. */
  forgetAccountEmail(): void {
    try {
      localStorage.removeItem(PasskeyDeviceHintsService.accountEmailKey);
    } catch {
      // Nothing to do.
    }
  }

  /**
   * Drops the hint after the server rejected the assertion - the passkey was
   * deleted server-side, or belongs to another environment. Without this the
   * login page would keep prompting for a credential that can never succeed.
   */
  forget(email: string): void {
    const normalized = this.normalize(email);
    if (!normalized) {
      return;
    }

    this.write(this.read().filter((hint) => hint.email !== normalized));
  }

  private update(
    email: string,
    change: (hint: PasskeyDeviceHint) => PasskeyDeviceHint,
  ): void {
    const normalized = this.normalize(email);
    if (!normalized) {
      return;
    }

    const others = this.read().filter((hint) => hint.email !== normalized);
    const current = this.read().find((hint) => hint.email === normalized) ?? {
      email: normalized,
    };

    this.write(
      [change(current), ...others]
        .sort((a, b) => this.timestamp(b) - this.timestamp(a))
        .slice(0, PasskeyDeviceHintsService.maxEntries),
    );
  }

  private read(): PasskeyDeviceHint[] {
    try {
      const raw = localStorage.getItem(PasskeyDeviceHintsService.storageKey);
      if (!raw) {
        return [];
      }

      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(
          (entry): entry is PasskeyDeviceHint =>
            Boolean(entry) &&
            typeof entry === 'object' &&
            typeof (entry as PasskeyDeviceHint).email === 'string' &&
            Boolean((entry as PasskeyDeviceHint).email),
        )
        .map((entry) => ({ ...entry, email: this.normalize(entry.email) }));
    } catch {
      // Private mode, blocked storage or a corrupt value: behave like a device
      // that has never seen a passkey.
      return [];
    }
  }

  private write(hints: PasskeyDeviceHint[]): void {
    try {
      localStorage.setItem(
        PasskeyDeviceHintsService.storageKey,
        JSON.stringify(hints),
      );
    } catch {
      // Nothing to do: the hint is a convenience, never a requirement.
    }
  }

  private timestamp(hint: PasskeyDeviceHint): number {
    const value = hint.lastUsedAt ?? hint.enrolledAt ?? hint.autoLoginAttemptedAt;
    const parsed = value ? Date.parse(value) : NaN;

    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private normalize(email: string): string {
    return email.trim().toLowerCase();
  }
}
